import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { fetchOddsFromGoogleSheets } from './utils/oddsUtils';

// Components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import PlayerProfile from './pages/PlayerProfile';
import TeamStats from './pages/TeamStats';
import DefenseVsPosition from './pages/DefenseVsPosition';
import HitRates from './pages/HitRates';
import TeamDashboard from './pages/TeamDashboard';
import VenueStats from './pages/VenueStats';
import BoxScoreGenerator from './pages/BoxScoreGenerator';
import Matchups from './pages/Matchups';


// Create Supabase client
const supabase = createClient(
  'https://jswjsgwvcqgslhjeshwl.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Impzd2pzZ3d2Y3Fnc2xoamVzaHdsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzgxOTIzMzEsImV4cCI6MjA1Mzc2ODMzMX0.lxNggl8wgBkp1Z_ilR1ydw85oaSKKVJfnCIMcFOHTaU'
);

// Basic connection test
const testSupabaseConnection = async () => {
  try {
    console.log('Testing Supabase connection to nrl_stats table...');
    
    // Test query
    const { data, error } = await supabase
      .from('nrl_stats')
      .select('id')
      .limit(1);
    
    if (error) {
      console.error('❌ Supabase connection failed:', {
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      return false;
    }

    console.log('✅ Supabase connection successful:', {
      connected: true,
      dataReceived: !!data
    });
    return true;
  } catch (err) {
    console.error('❌ Unexpected error testing Supabase:', {
      name: err.name,
      message: err.message
    });
    return false;
  }
};

// Run the test
testSupabaseConnection();

function App() {
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [players, setPlayers] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [selectedMatchup, setSelectedMatchup] = useState(null);
  const [showHomeTeam, setShowHomeTeam] = useState(true);
  const [oddsData, setOddsData] = useState([]);

  // Effect to handle dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // Function to close sidebar (for mobile)
  const closeSidebar = () => {
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Modify the cache validation function to force a fresh load
  const isCacheValid = (timestamp) => {
    // Temporarily return false to force a fresh load
    return false;
    
    // Original code
    // const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    // return Date.now() - timestamp < CACHE_DURATION;
  };

  // Function to clean up old cache entries
  const cleanupCache = () => {
    try {
      const meta = localStorage.getItem('nrl_stats_cache_meta');
      if (meta) {
        const { chunks } = JSON.parse(meta);
        // Remove old chunks
        for (let i = 0; i < chunks; i++) {
          localStorage.removeItem(`nrl_stats_cache_${i}`);
        }
      }
      localStorage.removeItem('nrl_stats_cache_meta');
      localStorage.removeItem('nrl_stats_cache'); // Remove old format cache if it exists
    } catch (error) {
      console.warn('Error cleaning up cache:', error);
    }
  };

  const loadAllData = async () => {
    try {
      console.log('Starting data fetch process...');
      
      // Check cache first
      const cachedData = localStorage.getItem('nrl_stats_cache_meta');
      if (cachedData) {
        const { timestamp, chunks } = JSON.parse(cachedData);
        if (isCacheValid(timestamp)) {
          // Load data from chunks
          let allData = [];
          for (let i = 0; i < chunks; i++) {
            const chunkData = localStorage.getItem(`nrl_stats_cache_${i}`);
            if (chunkData) {
              allData = allData.concat(JSON.parse(chunkData));
            }
          }
          
          if (allData.length > 0) {
            console.log('Using cached data:', {
              timestamp: new Date(timestamp).toLocaleString(),
              recordCount: allData.length,
              sampleRecord: allData[0]
            });
            setPlayers(allData);
            setLoadingProgress(100);
            setLoading(false);
            return;
          }
        } else {
          console.log('Cache expired, fetching fresh data...');
          cleanupCache(); // Clean up old cache before fetching new data
        }
      }

      // Function to fetch a single batch of data with range
      const fetchBatch = async (range) => {
        const { data, error } = await supabase
          .from('nrl_stats')
          .select('*')
          .range(range.start, range.end);

        if (error) {
          throw new Error(`Error loading stats batch for range ${range.start}-${range.end}: ${error.message}`);
        }

        return data || [];
      };

      // Function to fetch data with optimized parallel requests
      const fetchAllStats = async () => {
        const BATCH_SIZE = 1000;           // Keep batch size manageable
        const CONCURRENT_REQUESTS = 5;      // Reduce concurrent requests to be safer
        let offset = 0;
        let hasMore = true;
        const batches = [];

        while (hasMore) {
          const batchGroup = [];
          for (let j = 0; j < CONCURRENT_REQUESTS; j++) {
            const start = offset + (j * BATCH_SIZE);
            const end = start + BATCH_SIZE - 1;
            batchGroup.push(fetchBatch({ start, end }));
          }

          const results = await Promise.all(batchGroup);
          const newRecords = results.flat();
          batches.push(...newRecords);
          
          // Update progress (estimate based on expected total)
          const progress = Math.min(100, Math.round((batches.length / 11000) * 100));
          setLoadingProgress(progress);
          
          // Check if any of the batches had data
          const anyBatchHasData = results.some(batch => batch.length > 0);
          if (!anyBatchHasData) {
            hasMore = false;
          } else {
            offset += BATCH_SIZE * CONCURRENT_REQUESTS;
          }
        }

        // Remove duplicates and sort by ID
        const uniqueRecords = [...new Map(batches.map(item => [item.id, item])).values()]
          .sort((a, b) => a.id.localeCompare(b.id));

        console.log('Total records before deduplication:', batches.length);
        console.log('Total unique records:', uniqueRecords.length);

        return uniqueRecords;
      };

      const statsData = await fetchAllStats();
      console.log('Total records fetched:', statsData.length);

      if (!statsData || statsData.length === 0) {
        console.error('No data received from Supabase');
        throw new Error('No data received from Supabase');
      }

      // Process the records
      const processedRecords = statsData.map(record => ({
        ...record,
        mins_played: record.mins_played || '0:00'
      }));

      setPlayers(processedRecords);
      setLoadingProgress(100);
      
      // Cache the data in chunks
      try {
        const CHUNK_SIZE = 500; // Reduce chunk size
        const MAX_CHUNKS = 5; // Limit number of chunks
        const chunks = Math.min(Math.ceil(processedRecords.length / CHUNK_SIZE), MAX_CHUNKS);
        
        // Clean up old cache first
        cleanupCache();
        
        // Store metadata
        localStorage.setItem('nrl_stats_cache_meta', JSON.stringify({
          timestamp: Date.now(),
          chunks: chunks
        }));
        
        // Store only recent data
        const recentRecords = processedRecords.slice(-chunks * CHUNK_SIZE);
        
        // Store data chunks
        for (let i = 0; i < chunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = start + CHUNK_SIZE;
          const chunk = recentRecords.slice(start, end);
          localStorage.setItem(`nrl_stats_cache_${i}`, JSON.stringify(chunk));
        }
        
        console.log('Data successfully cached and loaded');
      } catch (cacheError) {
        // If caching fails, just log it - the app can still function without cache
        console.warn('Failed to cache data:', cacheError);
      }

    } catch (error) {
      console.error('Error in data loading process:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      // Try to load from cache as fallback if fetch fails
      try {
        let allData = [];
        const cachedMeta = localStorage.getItem('nrl_stats_cache_meta');
        if (cachedMeta) {
          const { chunks } = JSON.parse(cachedMeta);
          for (let i = 0; i < chunks; i++) {
            const chunkData = localStorage.getItem(`nrl_stats_cache_${i}`);
            if (chunkData) {
              allData = allData.concat(JSON.parse(chunkData));
            }
          }
          if (allData.length > 0) {
            console.log('Using cached data as fallback:', {
              recordCount: allData.length,
              sampleRecord: allData[0]
            });
            setPlayers(allData);
          }
        }
      } catch (cacheError) {
        console.error('Failed to load from cache:', {
          name: cacheError.name,
          message: cacheError.message,
          stack: cacheError.stack
        });
      }
    } finally {
      setLoading(false);
      console.log('Data loading process completed');
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // Add fixture loading
  useEffect(() => {
    const loadFixtures = async () => {
      try {
        const response = await fetch('/fixture.csv');
        const csvText = await response.text();
        
        // Parse CSV
        const lines = csvText.split('\n');
        const headers = lines[0].split(',');
        
        const fixtureData = lines.slice(1).map(line => {
          const values = line.split(',');
          return headers.reduce((obj, header, index) => {
            obj[header.trim()] = values[index]?.trim();
            return obj;
          }, {});
        }).filter(fixture => fixture['Match Number']); // Filter out empty rows
        
        setFixtures(fixtureData);
      } catch (error) {
        console.error('Error loading fixtures:', error);
      }
    };

    loadFixtures();
  }, []);

  // Add this effect to fetch odds data
  useEffect(() => {
    const fetchOdds = async () => {
      try {
        console.log('Fetching odds data...');
        const odds = await fetchOddsFromGoogleSheets();
        console.log('Odds data received:', {
          count: odds?.length,
          sample: odds?.slice(0, 3)
        });
        setOddsData(odds);
      } catch (error) {
        console.error('Error fetching odds:', error);
        setOddsData([]);
      }
    };

    fetchOdds();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen dark:bg-gray-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-accent mb-4 dark:border-accent-light"></div>
        <div className="text-lg font-medium text-gray-700 dark:text-gray-200">Loading data... {loadingProgress}%</div>
        <div className="w-64 h-2 bg-gray-200 dark:bg-gray-700 rounded-full mt-2">
          <div 
            className="h-full bg-accent dark:bg-accent-light rounded-full transition-all duration-300"
            style={{ width: `${loadingProgress}%` }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen h-screen overflow-hidden dark:bg-gray-900">
      <Header 
        onMenuClick={toggleSidebar} 
        isDarkMode={isDarkMode} 
        setIsDarkMode={setIsDarkMode}
        players={players} 
      />
      
      <div className="flex flex-1 min-h-0">
        {/* Sidebar overlay for mobile */}
        <div 
          className={`
            fixed inset-0 bg-black bg-opacity-50 z-20 transition-opacity duration-200
            md:hidden ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
          onClick={toggleSidebar}
        />
        
        {/* Sidebar */}
        <div 
          className={`
            fixed md:relative bg-white dark:bg-gray-800 z-30 h-[calc(100vh-64px)]
            transform transition-all duration-300 ease-in-out
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}
        >
          <Sidebar 
            players={players}
            fixtures={fixtures}
            onPlayerClick={closeSidebar}
          />
        </div>

        {/* Main content */}
        <main className="flex-1 overflow-x-hidden bg-gray-50 dark:bg-gray-900 min-w-0 transition-all duration-300 ease-in-out">
          <div className="h-full w-full px-4 py-4">
            <Routes>
              <Route 
                path="/" 
                element={
                  players && players.length > 0 ? (
                    <Navigate 
                      to={`/player/${encodeURIComponent(players[Math.floor(Math.random() * players.length)].player_name)}`} 
                      replace 
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-lg text-gray-700 dark:text-gray-200">Loading...</div>
                    </div>
                  )
                } 
              />
              <Route 
                path="/player/:playerId" 
                element={<PlayerProfile 
                  players={players}
                  fixtures={fixtures}
                  oddsData={oddsData}
                  selectedMatchId={selectedMatchId}
                  isDarkMode={isDarkMode}
                  selectedMatchup={selectedMatchup}
                  showHomeTeam={showHomeTeam}
                />} 
              />
              <Route
                path="/teamstats"
                element={<TeamStats players={players} isDarkMode={isDarkMode} />}
              />
              <Route
                path="/defense-vs-position"
                element={<DefenseVsPosition players={players} isDarkMode={isDarkMode} />}
              />
              <Route
                path="/hit-rates"
                element={<HitRates 
                  players={players} 
                  fixtures={fixtures}
                  isDarkMode={isDarkMode} 
                />}
              />
              <Route
                path="/team-dashboard/:teamName"
                element={<TeamDashboard players={players} isDarkMode={isDarkMode} />}
              />
              <Route
                path="/team-dashboard"
                element={<TeamDashboard players={players} isDarkMode={isDarkMode} />}
              />
              <Route
                path="/venue-stats"
                element={<VenueStats players={players} isDarkMode={isDarkMode} />}
              />
              <Route
                path="/box-score"
                element={<BoxScoreGenerator players={players} isDarkMode={isDarkMode} />}
              />
              <Route
                path="/matchups"
                element={<Matchups 
                  players={players} 
                  fixtures={fixtures}
                  isDarkMode={isDarkMode} 
                />}
              />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App; 